<template>
  <div class="blog-saved-page">
    <BlogHeaderMobile :query="''" />

    <main class="blog-saved-main">
      <div aria-live="polite" class="sr-only">{{ announcement }}</div>

      <nav class="blog-saved-back" aria-label="Voltar ao portal zinnes">
        <router-link :to="{ name: 'BlogHome' }">
          <v-icon small>mdi-arrow-left</v-icon> Voltar ao Portal Zinnes
        </router-link>
      </nav>

      <header class="blog-saved-header">
        <p class="blog-saved-eyebrow">SUA COLEÇÃO</p>
        <h1 class="blog-saved-title">Publicações salvas</h1>
        <p class="blog-saved-lead">Matérias que você guardou para ler quando quiser.</p>
      </header>

      <div v-if="loading" class="blog-saved-state" aria-live="polite">
        <v-progress-circular indeterminate color="#ffcf5a" />
        <p>Carregando publicações salvas…</p>
      </div>

      <div v-else-if="loadError" class="blog-saved-state blog-saved-state--error" role="alert">
        <v-icon large color="#f25568">mdi-alert-circle-outline</v-icon>
        <p>{{ loadError }}</p>
        <v-btn outlined color="#ffcf5a" @click="load(true)">Tentar novamente</v-btn>
      </div>

      <div v-else-if="!posts.length" class="blog-saved-state">
        <v-icon large color="rgba(255, 255, 255, 0.4)">mdi-bookmark-multiple-outline</v-icon>
        <h2 class="blog-saved-state__title">Nenhuma publicação salva</h2>
        <p class="blog-saved-state__message">Use o ícone de marcador em uma matéria para encontrá-la aqui.</p>
        <router-link class="zinnes-primary-btn blog-saved-state__action" :to="{ name: 'BlogHome' }">
          Explorar o Portal
        </router-link>
      </div>

      <template v-else>
        <div class="blog-saved-grid">
          <BlogSavedCard
            v-for="post in posts"
            :key="post.id"
            :post="post"
            :removing="removingId === post.id"
            @remove="removePost"
          />
        </div>

        <div v-if="moreError" class="blog-saved-more-error" role="alert">
          <p>{{ moreError }}</p>
          <button type="button" class="blog-saved-more-error__retry" :disabled="loadingMore" @click="load(false)">
            Tentar novamente
          </button>
        </div>

        <div v-if="loadingMore" class="blog-saved-state blog-saved-state--inline" aria-live="polite">
          <v-progress-circular indeterminate size="24" color="#ffcf5a" />
          <p>Carregando mais…</p>
        </div>

        <button
          v-if="hasMore && !loadingMore"
          type="button"
          class="blog-load-more"
          @click="load(false)"
        >
          Carregar mais matérias
        </button>
      </template>
    </main>

    <v-snackbar v-model="snackbar" color="#31263a" bottom :timeout="5000">
      {{ message }}
      <template #action="{ attrs }">
        <v-btn text color="#f6c945" v-bind="attrs" @click="snackbar = false">Fechar</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import BlogHeaderMobile from "@/components/blog/BlogHeaderMobile.vue";
import BlogSavedCard from "@/components/blog/BlogSavedCard.vue";
import { blogErrorMessage, getSavedBlogPosts, unsaveBlogPost } from "@/server/BlogService";
import "@/styles/blog-page.css";

const SAVED_PAGE_SIZE = 12;

export default {
  name: "BlogSavedView",
  components: { BlogHeaderMobile, BlogSavedCard },
  data: () => ({
    posts: [],
    cursor: null,
    hasMore: false,
    loading: false,
    loadingMore: false,
    loadError: "",
    moreError: "",
    removingId: null,
    announcement: "",
    snackbar: false,
    message: "",
  }),
  created() {
    this.applyMeta();
    this.load(true);
  },
  beforeDestroy() {
    document.title = "Zinnes";
  },
  methods: {
    applyMeta() {
      document.title = "Publicações salvas | Portal Zinnes";
      let meta = document.head.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", "description");
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", "Matérias que você guardou para ler quando quiser.");
    },
    async load(reset = true) {
      if (reset) {
        this.loading = true;
        this.loadError = "";
        this.posts = [];
        this.cursor = null;
        this.hasMore = false;
      } else {
        this.loadingMore = true;
        this.moreError = "";
      }

      try {
        const body = await getSavedBlogPosts({
          cursor: reset ? null : this.cursor,
          perPage: SAVED_PAGE_SIZE,
        });
        const page = body.data;
        const items = page.items || [];
        if (reset) {
          this.posts = items;
        } else {
          const map = new Map(this.posts.map((item) => [item.id, item]));
          items.forEach((item) => map.set(item.id, item));
          this.posts = Array.from(map.values());
        }
        this.cursor = page.next_cursor;
        this.hasMore = Boolean(page.has_more);
      } catch (error) {
        if (reset) {
          this.loadError = blogErrorMessage(error, "Não foi possível carregar as publicações salvas.");
        } else {
          this.moreError = blogErrorMessage(error, "Não foi possível carregar mais publicações salvas.");
        }
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },
    async removePost(post) {
      if (this.removingId !== null) return;
      this.removingId = post.id;
      try {
        await unsaveBlogPost(post.id);
        this.posts = this.posts.filter((item) => item.id !== post.id);
        this.notify("Publicação removida dos salvos.");
        this.announcement = "Publicação removida dos salvos.";
        if (!this.posts.length) {
          this.cursor = null;
          this.hasMore = false;
        }
      } catch {
        this.notify("Não foi possível remover a publicação dos salvos. Tente novamente.");
        this.announcement = "Não foi possível remover a publicação dos salvos. Tente novamente.";
      } finally {
        this.removingId = null;
      }
    },
    notify(message) {
      this.message = message;
      this.snackbar = true;
    },
  },
};
</script>